import api, { ApiResponse } from '.'
import { WorkerCounterEventLogSchema } from '../../../backend/src/services/resources/event-logs/event-log.model'
import { WorkerCounterSchema } from '../../../backend/src/services/resources/worker-counters/worker-counter.types'
import { CreateOrGetControllerParams } from '../../../backend/src/services/resources/worker-counters/worker-counters.controller.service.types'
import { Employer } from './organizations'
import { User } from './users'
import { WorkerCounterTemplate } from './worker-counter-templates'

/**
 * Interface for worker counter with proper typing
 */
export interface WorkerCounter
  extends Omit<WorkerCounterSchema, 'lastIncrementedBy' | 'template' | 'employer' | 'worker'> {
  _id: string
  lastIncrementedBy: User | undefined
  template: WorkerCounterTemplate
  employer: Employer
  worker: User
  history: (Omit<WorkerCounterEventLogSchema, '_id'> & { _id: string })[]
}

/**
 * Parameters for fetching worker counters
 */
export interface FetchWorkerCountersParams {
  worker?: string
  employer?: string
  template?: string
  isActive?: boolean
  filter?: string
}

const POPULATE_FIELDS = ['worker', 'employer', 'template', 'lastIncrementedBy']

/**
 * Fetch worker counters with optional filters (list action)
 */
export const fetch = async (
  params: FetchWorkerCountersParams,
): Promise<ApiResponse<WorkerCounter[]>> => {
  return await api({
    endpoint: '/worker-counters',
    method: 'get',
    params: {
      sort: '-createdAt',
      populate: POPULATE_FIELDS,
      ...params,
    },
  })
}

/**
 * Get a specific worker counter by ID
 */
export const get = async (id: string): Promise<ApiResponse<WorkerCounter>> => {
  return await api({
    endpoint: `/worker-counters/${id}`,
    method: 'GET',
    params: {
      populate: POPULATE_FIELDS,
    },
  })
}

/**
 * Activate a worker counter
 */
export const activate = async (id: string): Promise<ApiResponse<WorkerCounter>> => {
  return await api({
    endpoint: `/worker-counters/${id}/activate`,
    method: 'put',
  })
}

/**
 * Deactivate a worker counter
 */
export const deactivate = async (id: string): Promise<ApiResponse<WorkerCounter>> => {
  return await api({
    endpoint: `/worker-counters/${id}/deactivate`,
    method: 'put',
  })
}

/**
 * Paramètres pour ajuster la valeur d'un worker counter
 */
export interface AdjustValueParams {
  id: string
  value: number
}

/**
 * Ajuste la valeur d'un worker counter
 */
export const adjustValue = async ({
  id,
  value,
}: AdjustValueParams): Promise<ApiResponse<WorkerCounter>> => {
  return await api({
    endpoint: `/worker-counters/${id}/adjust-value`,
    method: 'put',
    params: { value },
  })
}

export type CreateOrGetWorkerCounterParams = CreateOrGetControllerParams

/**
 * Create or get a virtual counter for an organization
 */
export const createOrGet = async (
  params: CreateOrGetWorkerCounterParams,
): Promise<ApiResponse<WorkerCounter>> => {
  return await api({
    endpoint: '/worker-counters/create-or-get',
    method: 'post',
    params,
  })
}
